import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { useTranslate } from 'react-polyglot';
import styles from './BreedingDogs.module.scss';
import PublicTable from '../../Shared/PublicTable/PublicTable';
import { Form, Modal, Button } from 'react-bootstrap';
import backgroundImage from '../../../Assets/Images/background.svg';
import ExternalHeader from '../ExternalHeader/ExternalHeader';
import BreedDropdown from '../../Shared/BreedDropdown/BreedDropdown';
import RangeSlider from './RangeSlider';
import { useAllDogs, useBreedingDogs, useSimulateBreeding, useBreedregSchema } from '../../../hooks/useBreedingDogsQueries';
import { useQuery } from '@tanstack/react-query';
import DogSelectionModal from './DogSelectionModal';
import { ExternalLayoutContext } from '../ExternalLayout';

import postalCodesMap from '../../../Assets/Files/postnummerregister';
import { useAxios } from '../../../AxiosHandler';
import PageTable from '../../Shared/PageTable/PageTable';
import Field from '../../Shared/Wizard/Field/Field';
import DynSchema from '../../PublicSchema/DynSchema/DynSchema';

function BreedingDogs() {
    const t = useTranslate();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;

    const { breeds, selectedBreed, handleBreedChange, setBackLink } = useOutletContext<ExternalLayoutContext>();

    const [selectedDog, setSelectedDog] = useState<any>(null);
    const [showDogSelectionModal, setShowDogSelectionModal] = useState(false);

    // Search state - used for both server-side (no dog selected) and client-side (dog selected) filtering
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    
    // Filter states - only used when a dog is selected (client-side filtering)
    const [dynamicFilters, setDynamicFilters] = useState<any[]>([]);
    const [filterStates, setFilterStates] = useState<{ [key: string]: any }>({});
    
    // Server-side pagination for main list (when no dog is selected)
    const [serverPage, setServerPage] = useState(0); // 0-based page number
    const [serverPageSize, setServerPageSize] = useState(15);
    const [totalDogsCount, setTotalDogsCount] = useState(0);

    // Sort state — lifted out of PublicTable so it can drive server-sort or pre-paginate client sort
    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Breeding request schema states
    const [breedingRequestSchemaName, setBreedingRequestSchemaName] = useState<string>('');
    const [breedingRequestSchemaPhone, setBreedingRequestSchemaPhone] = useState<string>('');
    const [breedingRequestSchemaEmail, setBreedingRequestSchemaEmail] = useState<string>('');
    const [breedingRequestSchemaAcceptEthics, setBreedingRequestSchemaAcceptEthics] = useState<any>(null);
    const [breedingRequestSchemaAcceptPublishing, setBreedingRequestSchemaAcceptPublishing] = useState<any>(null);
    const [breedingRequestSchemaAcceptInformation, setBreedingRequestSchemaAcceptInformation] = useState<any>(null);
    const [sending, setSending] = useState(false);

    const [hasSearched, setHasSearched] = useState(false);
    const [selectDog, setSelectDog] = useState(false);

    const [selectedTraits, setSelectedTraits] = React.useState<string[]>(JSON.parse(localStorage.getItem("externalportal.breeding.selectedTraits." + selectedBreed) ?? "[]"));
    const [availableTraits, setAvailableTraits] = React.useState<Array<any>>([
        {
            COLUMNDBNAME: "ATAKSI_RESULT",
            CUSTOMDATA_JSON: null,
            DATATYPE: "string",
            ID: 93,
            LINEARFILTERING: 0,
            NAME: "ataksi",
            OPTIONS: "Fri,Bærer,Affisert,Ikke testet",
            PUBLICVALUE: 1
        },
    ]);

    const [showTraitsModal, setShowTraitsModal] = React.useState<boolean>(false);

    const [specialFiltersOpen, setSpecialFiltersOpen] = useState(false);
    const [specialfiltersfield, setSpecialfiltersfield] = React.useState<string>("");
    const [specialfiltersfrom, setSpecialfiltersfrom] = React.useState<string>("");
    const [specialfiltersto, setSpecialfiltersto] = React.useState<string>("");
    const [specialfilters, setSpecialfilters] = React.useState<any[]>(JSON.parse(localStorage.getItem("externaldogs.specialfilters." + selectedBreed) ?? "[]"));

    // Client-side special filters for when a dog is selected
    const [clientSpecialFiltersOpen, setClientSpecialFiltersOpen] = useState(false);
    const [clientSpecialFiltersField, setClientSpecialFiltersField] = React.useState<string>("");
    const [clientSpecialFiltersFrom, setClientSpecialFiltersFrom] = React.useState<string>("");
    const [clientSpecialFiltersTo, setClientSpecialFiltersTo] = React.useState<string>("");
    const [clientSpecialFilters, setClientSpecialFilters] = React.useState<any[]>([]);

    const [showOnlyApproveddogs, setShowOnlyApproveddogs] = React.useState<boolean>(localStorage.getItem("externaldogs.showOnlyApproveddogs") == "true" ? true : false);
    const [includeDogsOnWait, setIncludeDogsOnWait] = React.useState<boolean>(localStorage.getItem("externaldogs.includeDogsOnWait") == "true" ? true : false);
    const [selectedDiscipline, setSelectedDiscipline] = React.useState<'all' | 'leash' | 'loose'>((localStorage.getItem("externaldogs.discipline") as 'all' | 'leash' | 'loose') || 'all');

    const { data: breedRegSchema } = useBreedregSchema(selectedBreed);

    const [breedingRequestSchemaAnswers, setBreedingRequestSchemaAnswers] = useState<{ [key: string]: any }>({});

    function submitBreedingRequest() {
        setSending(true);

        let dam = selectedDog.sex == "female" ? selectedDog : showBreedingRequestModal;
        let sire = selectedDog.sex == "male" ? selectedDog : showBreedingRequestModal;

        return new Promise((resolve, reject) => {
            axios.single({
                method: 'put',
                url: "/api/dynschema/" + selectedBreed + "/" + breedRegSchema?.ID,
                data:{
                    "SPECIAL_SCHEMA_TYPE": "malepartnerreq",
                    "questions": [

                    ]
                },
                responseType: 'json',
            }).then((res: any) => {
                resolve(res);
            }).catch((err: any) => {
                console.log(err);
                reject(err);
            }).finally(() => {
                setSending(false);
            });
        });
    }
    
    // Reset to first page when filters or sort change
    useEffect(() => {
        if (!selectedDog) {
            setServerPage(0);
        } else {
            setCurrentPage(1);
        }
    }, [selectedDog, debouncedSearchTerm, sortField, sortOrder]);
    
    // React Query hooks
    const skip = serverPage * serverPageSize;
    
    // Use allDogs for main list with server-side pagination/filtering when no dog is selected
    const { data: allDogsResponse, isLoading: allDogsLoading, error: allDogsError } = useAllDogs(
        selectedBreed,
        undefined,
        sortField ? { field: sortField, order: sortOrder } : null,
        serverPageSize,
        skip
    );
    const { data: breedingDogsData = [], isLoading: breedingDogsLoading, error: breedingDogsError } = useBreedingDogs(selectedBreed, !!selectedDog);
    
    // Extract dogs and total from response
    const allDogsData = allDogsResponse?.dogs || [];
    const allDogsTotal = allDogsResponse?.total || 0;
    
    useEffect(() => {
        if (!selectedDog && allDogsTotal > 0) {
            setTotalDogsCount(allDogsTotal);
        }
    }, [allDogsTotal, selectedDog]);
    
    // Use allDogs when no dog is selected, breedingDogs when a dog is selected for simulation
    const rawDogs = selectedDog ? breedingDogsData : allDogsData;
    const dogsLoading = selectedDog ? breedingDogsLoading : allDogsLoading;
    
    // Fetch column information from API - different endpoints based on whether a dog is selected
    const { data: columnInfo = [], isLoading: columnInfoLoading } = useQuery({
        queryKey: ['columnsinfo', selectedBreed, selectedDog ? 'breeding' : 'breeding_dogs'],
        queryFn: async () => {
            if (!selectedBreed) return [];
            const endpoint = selectedDog 
                ? `/api/dogs/breedingcolumnsinfo/${selectedBreed}` 
                : `/api/dogs/columnsinfo/${selectedBreed}/breeding_dogs`;
            const response = await fetch(endpoint);
            if (!response.ok) throw new Error('Failed to fetch column info');
            const data = await response.json();
            
            // Normalize API responses to match expected structure
            if (Array.isArray(data)) {
                if (selectedDog) {
                    // Normalize breeding column info (when dog is selected) - uses PBC_ and PBCI_ prefixes
                    return data
                        .filter((item: any) => item.PBC_STATUS === 'enabled' && item.PBCI_ID !== null) // Only include enabled columns with info
                        .map((item: any) => ({
                            COLUMN_IDENTIFIER: item.PBC_COLUMN_IDENTIFIER,
                            LABEL: item.PBCI_LABEL || '',
                            COLUMN_GROUP: item.PBCI_COLUMN_GROUP || '', 
                            DEFAULT_VISIBLE: item.PBCI_DEFAULT_VISIBLE !== null ? item.PBCI_DEFAULT_VISIBLE : 0,
                            ORDER: item.PBCI_ORDER !== null ? item.PBCI_ORDER : 0,
                            TOOLTIP: item.PBCI_TOOLTIP || '',
                            OPTIONS: item.OPTIONS || ''
                        }));
                } else {
                    // Normalize columnsinfo response (breeding_dogs) - uses PCI_ prefixes
                    // The new structure has: ID (PCO_ID), PCI_ID (the config ID), PCI_PCO_ID (references ID), and PCI_ prefixed fields
                    return data
                        .filter((item: any) => item.STATUS === 'enabled' && item.PCI_ID !== null) // Only include enabled columns with info
                        .map((item: any) => ({
                            ID: item.PCI_ID, // Use PCI_ID as the ID (was the old ID field)
                            COLUMN_IDENTIFIER: item.COLUMN_IDENTIFIER,
                            LABEL: item.PCI_LABEL || '',
                            COLUMN_GROUP: item.PCI_COLUMN_GROUP || '',
                            DEFAULT_VISIBLE: item.PCI_DEFAULT_VISIBLE !== null ? item.PCI_DEFAULT_VISIBLE : 0,
                            ORDER: item.PCI_ORDER !== null ? item.PCI_ORDER : 0,
                            TOOLTIP: item.PCI_TOOLTIP || '',
                            OPTIONS: item.OPTIONS || ''
                        }));
                }
            }
            
            return data;
        },
        enabled: !!selectedBreed,
        staleTime: 30 * 1000, // 30 seconds - more responsive to API changes
    });
    
    const { dogs } = useMemo(() => {
        if (!rawDogs || !Array.isArray(rawDogs) || rawDogs.length === 0) {
            return { dogs: [] };
        }
        return { dogs: rawDogs };
    }, [rawDogs]);
    
    // UI states
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'card' | 'map'>('list');
    
    // Modal states
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [showBreedingRequestModal, setShowBreedingRequestModal] = useState<any>(null);

    // Function to analyze data and extract available columns
    const analyzeDataColumns = (data: any[], hasSelectedDog?: boolean) => {
        // If we don't have column info from API yet, return empty array
        if (!columnInfo || columnInfo.length === 0) {
            return [];
        }
        
        // Get all unique keys from the data
        const allKeys = new Set<string>();
        
        // Create column mappings from API data
        const columnMappings: {
            [key: string]: {
                label: string;
                visible: boolean;
                order: number;
                tooltip?: string;
                column_group?: string;
                options?: string[];
            };
        } = {};

        columnInfo.forEach((column: any) => {
            // Use COLUMN_IDENTIFIER field
            const key = column.COLUMN_IDENTIFIER;
            allKeys.add(key);
            // Clean up any trailing commas in the key
            const cleanKey = key?.replace(/,$/, '');
            columnMappings[cleanKey] = {
                label: column.LABEL,
                visible: column.DEFAULT_VISIBLE === 1,
                order: column.ORDER,
                tooltip: column.TOOLTIP,
                column_group: column.COLUMN_GROUP,
                options: column.OPTIONS
            };
        });
        
        // Always add NAME as default column if not present in API
        if (!columnMappings['NAME']) {
            columnMappings['NAME'] = {
                label: 'Navn',
                visible: true,
                order: 0, // First column
                tooltip: undefined // No tooltip for default columns
            };
        } else {
            columnMappings['NAME'].visible = true;
            columnMappings['NAME'].order = 0; // Ensure it's first
        }
        
        // Only add DATE_BIRTH as default column when a dog is selected (breeding simulation view)
        // When no dog is selected, DATE_BIRTH is already in the regular dog data
        if (hasSelectedDog) {
            // Add REGNO as first column for selected dog view
            if (!columnMappings['REGNO']) {
                columnMappings['REGNO'] = {
                    label: 'Reg.Nr',
                    visible: true,
                    // First column
                    order: 0,
                    tooltip: undefined // No tooltip for default columns
                };
            } else {
                columnMappings['REGNO'].visible = true;
                columnMappings['REGNO'].order = 0; // Ensure it's first
            }
            
            if (!columnMappings['DATE_BIRTH']) {
                columnMappings['DATE_BIRTH'] = {
                    label: 'Fødselsår',
                    visible: false,
                    // Second column
                    order: 1,
                    tooltip: undefined // No tooltip for default columns
                };
            } else {
                columnMappings['DATE_BIRTH'].visible = false;
                columnMappings['DATE_BIRTH'].order = 1; // Ensure it's second
                // Override the label to show "Alder" instead of "Fødselsdato"
                columnMappings['DATE_BIRTH'].label = 'Alder';
                // Keep original tooltip from API if it exists, otherwise undefined
                columnMappings['DATE_BIRTH'].tooltip = columnMappings['DATE_BIRTH'].tooltip || undefined;
            }
            
            if (!columnMappings['POSTAL_CODE']) { 
                let postPlaceOptions: string[] = [];

                // Add all unique post places for the postal codes in the data
                for (const entry of postalCodesMap.split('\n')) {
                    const parts = entry.split('\t');
                    if (parts[1]) {
                        if (!postPlaceOptions.includes(parts[1])) {
                            postPlaceOptions.push(parts[1]);
                        }
                    }
                }

                columnMappings['POSTAL_CODE'] = {
                    label: 'Postnummer',
                    visible: true,
                    options: postPlaceOptions,
                    column_group: 'Kontaktinfo',
                    // Last column
                    order: 999,
                    tooltip: undefined // No tooltip for default columns
                };
            }
        }
        
        // Set fixed orders based on context
        if (hasSelectedDog) {
            // Selected dog view: REGNO first, then NAME, then DATE_BIRTH
            if (columnMappings['REGNO']) {
                columnMappings['REGNO'].order = 0; // First
            }
            columnMappings['NAME'].order = 1; // Second
            if (columnMappings['DATE_BIRTH']) {
                columnMappings['DATE_BIRTH'].order = 2; // Third
            }
        } else {
            // Not selected dog view: NAME first, then DATE_BIRTH
            columnMappings['NAME'].order = 0; // Always first
        }
        
        // Filter to only include columns that actually exist in the data
        // For breeding columns, be more flexible with matching
        const availableColumns = Object.keys(columnMappings)
            .filter(key => {
                // Always include NAME, DATE_BIRTH, and REGNO (when dog is selected) even if not in data
                if (
                    key === 'NAME' ||
                    key === 'DATE_BIRTH' || 
                    (hasSelectedDog && key === 'REGNO') ||
                    (hasSelectedDog && key === 'POSTAL_CODE')
                ) {
                    return true;
                }
                
                const isExactMatch = allKeys.has(key);
                const isFlexibleMatch = hasSelectedDog && (key.includes('EBV') || key.includes('SNITT') || key.includes('RAB')) && 
                    Array.from(allKeys).some(dataKey => dataKey.includes(key) || key.includes(dataKey));
                
                return isExactMatch || isFlexibleMatch;
            })
            .map(key => ({
                id: key,
                label: columnMappings[key].label,
                visible: columnMappings[key].visible,
                order: columnMappings[key].order,
                tooltip: columnMappings[key].tooltip,
                column_group: columnMappings[key].column_group,
                options: columnMappings[key].options
            }))
            .sort((a, b) => a.order - b.order);
        
        return availableColumns;
    };
    
    // Column configuration for breeding dogs - will be populated dynamically
    const [availableColumns, setAvailableColumns] = useState<Array<{id: string, label: string, visible: boolean, order: number, tooltip?: string, column_group?: string}>>([]);

    // Open filter modal state
    const [openFilterModal, setOpenFilterModal] = useState<string | null>(null);
    
    // Cache keys
    const COLUMN_CACHE_KEY = 'breedingDogs_columnConfig_v3'; // Updated for simulated breeding columns
    const VIEW_MODE_CACHE_KEY = 'breedingDogs_viewMode';

    // Cache functions
    const saveColumnConfigToCache = (config: any[]) => {
        try {
            localStorage.setItem(COLUMN_CACHE_KEY, JSON.stringify(config));
        } catch (error) {
            
        }
    };

    const loadColumnConfigFromCache = (): any[] | null => {
        try {
            const cached = localStorage.getItem(COLUMN_CACHE_KEY);
            return cached ? JSON.parse(cached) : null;
        } catch (error) {
            return null;
        }
    };

    const saveViewModeToCache = (mode: 'list' | 'card' | 'map') => {
        try {
            localStorage.setItem(VIEW_MODE_CACHE_KEY, mode);
        } catch (error) {
            
        }
    };

    const loadViewModeFromCache = (): 'list' | 'card' | 'map' => {
        try {
            const cached = localStorage.getItem(VIEW_MODE_CACHE_KEY);
            return (cached === 'list' || cached === 'card' || cached === 'map') ? cached : 'list';
        } catch (error) {
            return 'list';
        }
    };

    // Pagination - for client-side when dog is selected
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    useEffect(() => {
        // Load cached column configuration
        const cachedColumns = loadColumnConfigFromCache();
        if (cachedColumns) {
            setAvailableColumns(cachedColumns);
        }
        
        // Load cached view mode
        const cachedViewMode = loadViewModeFromCache();
        setViewMode(cachedViewMode);


        // // If body is clicked while openFilterModal is true, close it
        // const handleBodyClick = (e: MouseEvent) => {
        //     // if target is not a div with class filterDropdown
        //     const target = e.target as HTMLElement;
        //     if (openFilterModal && !target.closest(`.${styles.filterDropdown}`)) {
        //         setOpenFilterModal(null);
        //     }
        // };
        // document.body.addEventListener('click', handleBodyClick);

        // return () => {
        //     document.body.removeEventListener('click', handleBodyClick);
        // };
    }, []);

    // Track if modal has been shown on initial load for current breed
    const hasShownModalRef = useRef<string>('');
    
    // Auto-open modal only on first page load when no dog is selected
    useEffect(() => {
        // Only show modal on initial load when:
        // - No dog is selected
        // - Dogs have loaded (length > 0)
        // - Not currently loading
        // - Haven't shown modal for this breed yet
        if (!selectedDog && selectedBreed) {
            if (hasShownModalRef.current !== selectedBreed) {
                setShowDogSelectionModal(true);
                hasShownModalRef.current = selectedBreed;
            }
        }
    }, [selectedDog, dogs.length, dogsLoading, selectedBreed]);
    
    // Reset modal flag when breed changes
    useEffect(() => {
        if (selectedBreed && hasShownModalRef.current !== selectedBreed) {
            hasShownModalRef.current = '';
        }
        // Reset discipline filter to 'all' when breed changes
        if (selectedDiscipline !== 'all') {
            setSelectedDiscipline('all');
            localStorage.setItem("externaldogs.discipline", 'all');
        }
    }, [selectedBreed]);

    useEffect(() => {
        setBackLink({ to: '/external', text: '← Tilbake til hovedsiden' });
        return () => setBackLink(undefined);
    }, []);

    useEffect(() => {
        if (params.breedid) {
            handleBreedChange(params.breedid);
        }
    }, [params.breedid]);

    // Get partner IDs for simulated breeding
    const partnerIds = useMemo(() => {
        if (!selectedDog || !dogs) return [];
        
        // Use REGNO as the unique identifier
        const ids = dogs.map(dog => {
            const regno = dog.REGNO;
            return regno?.toString();
        }).filter(Boolean);
        
        return ids;
    }, [selectedDog, dogs]);
    
    // Get selected dog REGNO as the unique identifier
    const selectedDogId = selectedDog?.REGNO?.toString() || '';
    
    
    // Simulated breeding hook
    const { data: simulatedBreedingData = [], isLoading: simulatedBreedingLoading, error: simulatedBreedingError } = useSimulateBreeding(
        selectedDogId, 
        partnerIds
    );


    // Create a map of simulated breeding results by partner ID
    const simulatedBreedingMap = useMemo(() => {
        const map: { [key: string]: any } = {};
        
        // Ensure we have an array
        const dataArray = Array.isArray(simulatedBreedingData) ? simulatedBreedingData : [];
        
        dataArray.forEach((result, index) => {            
            // The simulation data should contain breeding combinations
            // We need to identify which dog is the partner (not the selected dog)
            if (result.dam && result.dam.REGNO && result.sire && result.sire.REGNO) {
                const damRegno = result.dam.REGNO;
                const sireRegno = result.sire.REGNO;
                
                // Map the partner dog (the one that's not the selected dog)
                if (damRegno === selectedDogId && sireRegno !== selectedDogId) {
                    map[sireRegno] = result.offspring;
                } else if (sireRegno === selectedDogId && damRegno !== selectedDogId) {
                    map[damRegno] = result.offspring;
                }
            }
        });
        
        return map;
    }, [simulatedBreedingData]);
    
    // Function to generate dynamic filters based on visible columns and data
    const generateDynamicFilters = (data: any[], visibleColumns: any[]): any[] => {
        if (!data || data.length === 0 || !visibleColumns || visibleColumns.length === 0) {
            return [];
        }

        const filters: any[] = [];
        
        let dogsWithBreedingData = dogs.filter(dog => {
            const dogId = dog.REGNO;
            const simulatedData = simulatedBreedingMap[dogId?.toString()];
            return simulatedData; // Only include dogs with simulation data
        }).map(dog => {
            const dogId = dog.REGNO;
            const simulatedData = simulatedBreedingMap[dogId?.toString()];

            // postalName lookup from postalCodesData based on dog's POSTAL_CODE
            // postalCodesData is a raw txt file with tab separated values: POSTAL_CODE, POSTAL_AREA, POSTAL_NAME, etc.
            // Use regex to find matching POSTAL_AREA
            let postalName = '';
            const postalCode = dog.POSTAL_CODE?.toString() || '';

            // For each newline entry in postalCodesMap, split by tab and check if first part matches postalCode
            for (const entry of postalCodesMap.split('\n')) {
                const parts = entry.split('\t');
                if (parts[0] === postalCode) {
                    postalName = parts[1] || '';
                    break;
                }
            }
            
            // Merge simulated offspring data with original dog data
            return {
                ...dog,
                ...simulatedData, // simulatedData already contains offspring data at top level
                // Keep original dog name and basic info
                NAME: dog.NAME,
                REGNO: dogId,
                SEX: dog.SEX,
                DATE_BIRTH: dog.DATE_BIRTH,
                POST_PLACE: postalName,
                // Mark as simulated data
                _isSimulated: true,
                _originalDog: dog
            };
        });
        
        // Fields to exclude from filtering (since they have search functionality)
        const excludedFields = ['REGNO', 'NAME', 'OWNER_NAME', 'POSTAL_CODE', 'POSTAL_AREA', 'PHONE', 'EMAIL'];
        
        visibleColumns.forEach(column => {
            // Skip excluded fields
            if (excludedFields.includes(column.id)) {
                return;
            }
            
            const fieldName = column.id;
            
            if (
                column.options?.length == 0 ||
                column.options == '' ||
                column.options == null
            ) {

                let values = dogsWithBreedingData.map(dog => dog[fieldName]?.toString());
                
                // Determine min and max for range filter if numeric
                const numericValues = values.map(val => parseFloat(val)).filter(val => !isNaN(val));
                if (numericValues.length > 0) {
                    const min = Math.min(...numericValues);
                    const max = Math.max(...numericValues);
                    filters.push({
                        id: fieldName,
                        label: column.label,
                        type: 'range',
                        min: min,
                        max: max,
                        currentMin: min,
                        currentMax: max,
                        // If min or max are floating point, set step to 0.1, else 1
                        step: (Number(min) % 1 !== 0 || Number(max) % 1 !== 0) ? 0.1 : 1
                    });
                }

            } else {
                // Sample Options: "Fri,Bærer,Affisert,Ikke testet"

                // Generate all combinations of the comma-separated values in options with "/" separator
                // Sample "Fri/Bærer", "Fri/Fri", "Bærer/Affisert", "Affisert/Ikke testet", etc.
                let allCombinations: string[] = [];
                // const optionParts = ['Fri', 'Bærer', 'Affisert', 'Ikke testet']; //column?.options?.split?.(',');

                // optionParts?.forEach((part1: string) => {
                //     optionParts?.forEach((part2: string) => {
                //         allCombinations.push(`${part1}/${part2}`);
                //     });
                // });

                allCombinations = ['Fri/Fri', 'Bærer/Fri', 'Fri/Bærer', 'Ikke testet/Fri', 'Fri/Ikke testet'];

                if (allCombinations.length > 1 && allCombinations.length <= 50) {
                    filters.push({
                        id: fieldName,
                        label: column.label,
                        type: 'multiselect',
                        options: column.options?.length < 1 ? allCombinations.map(component => ({
                            value: component,
                            label: component
                        })) : column.options?.sort?.().map?.((opt: string) => ({
                            value: opt,
                            label: opt
                        })),
                        selectedValues: [],
                        isCommaSeparated: true // Flag to indicate this needs contains logic
                    });
                }
            }
        });
        
        return filters;
    };

    // Apply filters only when a dog is selected (client-side filtering for breeding dogs)
    // When no dog is selected, filtering is done server-side
    const filteredDogs = useMemo(() => {
        if (!dogs || dogs.length === 0) return [];
        
        // When no dog is selected, return dogs as-is (filtering is done server-side)
        if (!selectedDog) {
            return dogs;
        }

        let filtered = dogs.filter(dog => {
            const dogId = dog.REGNO;
            const simulatedData = simulatedBreedingMap[dogId?.toString()];
            return simulatedData; // Only include dogs with simulation data
        }).map(dog => {
            const dogId = dog.REGNO;
            const simulatedData = simulatedBreedingMap[dogId?.toString()];

            // postalName lookup from postalCodesData based on dog's POSTAL_CODE
            // postalCodesData is a raw txt file with tab separated values: POSTAL_CODE, POSTAL_AREA, POSTAL_NAME, etc.
            // Use regex to find matching POSTAL_AREA
            let postalName = '';
            const postalCode = dog.POSTAL_CODE?.toString() || '';

            // For each newline entry in postalCodesMap, split by tab and check if first part matches postalCode
            for (const entry of postalCodesMap.split('\n')) {
                const parts = entry.split('\t');
                if (parts[0] === postalCode) {
                    postalName = parts[1] || '';
                    break;
                }
            }

            // Merge simulated offspring data with original dog data
            return {
                ...dog,
                ...simulatedData, // simulatedData already contains offspring data at top level
                // Keep original dog name and basic info
                NAME: dog.NAME,
                REGNO: dogId,
                SEX: dog.SEX,
                DATE_BIRTH: dog.DATE_BIRTH,
                POSTAL_CODE: postalCode,
                POST_PLACE: postalName,
                // Mark as simulated data
                _isSimulated: true,
                _originalDog: dog
            };
        });
        
        // When dog is selected, apply client-side filtering (gender + dynamic filters)
        // let filtered = [...dogs];
        
        // Apply gender filtering
        const selectedDogSex = selectedDog.SEX;
        
        if (selectedDogSex === 'female' || selectedDogSex === 'H') {
            // If selected dog is female, show only male dogs for breeding
            filtered = filtered.filter(dog => dog.SEX === 'male' || dog.SEX === 'T');
        } else if (selectedDogSex === 'male' || selectedDogSex === 'T') {
            // If selected dog is male, show only female dogs for breeding
            filtered = filtered.filter(dog => dog.SEX === 'female' || dog.SEX === 'H');
        }
        
        // Apply dynamic filters (only when a dog is selected)
        dynamicFilters.forEach(filter => {
            const filterState = filterStates[filter.id];
            if (!filterState) return;
            
            if (filter.type === 'range') {
                const { min, max } = filterState;
                filtered = filtered.filter(dog => {
                    const value = parseFloat(dog[filter.id]);
                    if (isNaN(value)) return false;
                    return value >= min && value <= max;
                });
            } else if (filter.type === 'multiselect') {
                if (filterState.length > 0) {
                    filtered = filtered.filter(dog => {
                        const fieldValue = dog[filter.id]?.toString() || '';
                        return filterState.some((selectedValue: any) => 
                            fieldValue.includes(selectedValue)
                        );
                    });
                }
            }
        });

        return filtered;
    }, [dogs, selectedDog, dynamicFilters, filterStates, simulatedBreedingMap]);

    // Update columns dynamically based on available data
    useEffect(() => {
        // Always run when dogs or simulatedBreedingData changes
        const simulatedData = simulatedBreedingData ? Array.isArray(simulatedBreedingData) ? simulatedBreedingData : [] : [];
        
        // When a dog is selected, we need to analyze the merged data that includes simulated breeding columns
        let dataForColumnAnalysis;
        if (selectedDog && simulatedData.length > 0) {
            // Create the merged data that includes simulated breeding columns
            dataForColumnAnalysis = dogs.filter(dog => {
                const dogId = dog.REGNO;
                return simulatedData.some(result =>
                    (result.dam && result.dam.REGNO === dogId) ||
                    (result.sire && result.sire.REGNO === dogId)
                );
            }).map(dog => {
                const dogId = dog.REGNO;
                const simulatedResult = simulatedData.find(result =>
                    (result.dam && result.dam.REGNO === dogId) ||
                    (result.sire && result.sire.REGNO === dogId)
                );

                if (simulatedResult && simulatedResult.offspring) {
                    return {
                        ...dog,
                        ...simulatedResult.offspring,
                        NAME: dog.NAME,
                        REGNO: dogId,
                        SEX: dog.SEX,
                        DATE_BIRTH: dog.DATE_BIRTH
                    };
                }
                return dog;
            });
        } else {
            dataForColumnAnalysis = dogs || [];
        }
        
        // Always analyze columns - show different columns based on whether a dog is selected
        const dynamicColumns = analyzeDataColumns(dataForColumnAnalysis, !!selectedDog);
        
        // Check if columns have actually changed to prevent unnecessary updates
        const currentColumnIds = availableColumns.map(col => col.id).sort();
        const newColumnIds = dynamicColumns.map(col => col.id).sort();
        
        if (
            (JSON.stringify(currentColumnIds) !== JSON.stringify(newColumnIds)) || 
            Object.keys(simulatedBreedingMap).length > 0
        ) {
            setAvailableColumns(dynamicColumns);
            
            // Only generate dynamic filters when a dog is selected (for client-side filtering)
            if (selectedDog) {
                // Generate dynamic filters based on visible columns and current data (including simulated breeding data)
                const visibleColumns = dynamicColumns.filter(col => col.visible);

                const dataForFilters = dataForColumnAnalysis || []; // Use data that includes simulated breeding columns

                const newFilters = generateDynamicFilters(dataForFilters, visibleColumns);
                setDynamicFilters(newFilters);
            } else {
                // Clear filters when no dog is selected
                setDynamicFilters([]);
                setFilterStates({});
            }
        }
    }, [columnInfo, dogs, simulatedBreedingMap]);

    // Merge simulated breeding data with dog data
    // Is this needed anymore? 
    const dogsWithSimulatedData = useMemo(() => {
        if (!selectedDog || !filteredDogs) return filteredDogs;
        
        return filteredDogs;
    }, [filteredDogs, simulatedBreedingMap, selectedDog]);


    const loading = dogsLoading || simulatedBreedingLoading;
    const totalDogs = dogsWithSimulatedData.length;


    function getTableHeaders() {
        if (!availableColumns || availableColumns.length === 0) return [];
        
        const visibleColumns = availableColumns
            .filter(col => col.visible)
            .sort((a, b) => a.order - b.order);
        
        return visibleColumns.map(col => {
            // Use API data for label and tooltip
            const baseHeader: any = {
                label: col.label,
                field: col.id,
                column_group: col.column_group,
                sortActive: sortField === col.id,
                sortDirection: sortField === col.id ? sortOrder : 'asc',
                onSort: () => {
                    const isAsc = sortField === col.id && sortOrder === 'asc';
                    setSortOrder(isAsc ? 'desc' : 'asc');
                    setSortField(col.id);
                }
            };
            
            // Only add tooltip if it exists and is not null/undefined
            if ((col as any).tooltip && (col as any).tooltip !== null && (col as any).tooltip !== undefined) {
                baseHeader.tooltip = (col as any).tooltip;
            } else {
                // Explicitly set tooltip to null to prevent default tooltips
                baseHeader.tooltip = null;
            }
            
            // Add special handling for specific columns
            switch (col.id) {
                case 'DATE_BIRTH':
                    return { 
                        ...baseHeader,
                        override: (data: any): string => {

                            if (!data.DATE_BIRTH) return 'Ukjent';
                            const birthDate = new Date(data.DATE_BIRTH);
                            const today = new Date();
                            const ageInYears = today.getFullYear() - birthDate.getFullYear();
                            const monthDiff = today.getMonth() - birthDate.getMonth();
                            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                                return `${ageInYears - 1}`;
                            }
                            return `${ageInYears}`;
                        }
                    };
                default:
                    // Use API data for all other columns
                    return baseHeader;
            }
        });
    }

    function getPaginatedData() {
        // When no dog is selected, data is already paginated server-side
        if (!selectedDog) {
            return dogsWithSimulatedData;
        }
        
        // Apply client-side special filters when a dog is selected
        let filteredData = dogsWithSimulatedData;
        
        if (clientSpecialFilters && clientSpecialFilters.length > 0) {
            filteredData = dogsWithSimulatedData.filter(dog => {
                // Dog must pass all active filters
                return clientSpecialFilters.every(filter => {
                    const fieldValue = dog[filter.field];
                    
                    // Skip if field doesn't exist
                    if (fieldValue === undefined || fieldValue === null) {
                        return false;
                    }
                    
                    // Parse numeric values
                    const numericValue = parseFloat(fieldValue);
                    const fromValue = filter.from !== '' ? parseFloat(filter.from) : -Infinity;
                    const toValue = filter.to !== '' ? parseFloat(filter.to) : Infinity;
                    
                    // Check if value is within range
                    if (!isNaN(numericValue) && !isNaN(fromValue) && !isNaN(toValue)) {
                        return numericValue >= fromValue && numericValue <= toValue;
                    }

                    // Field has a filter applied but value couldn't be compared — exclude the row
                    return false;
                });
            });
        }
        
        // Sort full filtered dataset before paginating
        if (sortField) {
            filteredData = [...filteredData].sort((a, b) => {
                const aVal = a[sortField];
                const bVal = b[sortField];
                if (aVal === bVal) return 0;
                if (sortOrder === 'asc') return aVal < bVal ? -1 : 1;
                return bVal < aVal ? -1 : 1;
            });
        }

        // When dog is selected, use client-side pagination
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredData.slice(startIndex, endIndex);
    }


    function handleColumnToggle(columnId: string) {
        setAvailableColumns(prev => {
            const newColumns = prev.map(col => 
                col.id === columnId ? { ...col, visible: !col.visible } : col
            );
            saveColumnConfigToCache(newColumns);
            
            // Only regenerate dynamic filters when a dog is selected (for client-side filtering)
            if (selectedDog) {
                // Regenerate dynamic filters based on new visible columns
                const visibleColumns = newColumns.filter(col => col.visible);
                // Use the same data logic as the main useEffect
                const simulatedData = simulatedBreedingData ? Array.isArray(simulatedBreedingData) ? simulatedBreedingData : [] : [];
                let dataForFilters;
                if (simulatedData.length > 0) {
                    // Create the merged data that includes simulated breeding columns
                    dataForFilters = dogs.filter(dog => {
                        const dogId = dog.REGNO;
                        return simulatedData.some(result =>
                            (result.dam && result.dam.REGNO === dogId) ||
                            (result.sire && result.sire.REGNO === dogId)
                        );
                    }).map(dog => {
                        const dogId = dog.REGNO;
                        const simulatedResult = simulatedData.find(result =>
                            (result.dam && result.dam.REGNO === dogId) ||
                            (result.sire && result.sire.REGNO === dogId)
                        );

                        if (simulatedResult && simulatedResult.offspring) {
                            return {
                                ...dog,
                                ...simulatedResult.offspring,
                                NAME: dog.NAME,
                                REGNO: dogId,
                                SEX: dog.SEX,
                                DATE_BIRTH: dog.DATE_BIRTH
                            };
                        }
                        return dog;
                    });
                } else {
                    dataForFilters = dogs || [];
                }
                const newFilters = generateDynamicFilters(dataForFilters, visibleColumns);
                setDynamicFilters(newFilters);
                
                // Reset filter states when columns change
                setFilterStates({});
            }
            
            return newColumns;
        });
    }

    function handleColumnOrderChange(columnId: string, direction: 'up' | 'down') {
        setAvailableColumns(prev => {
            const newColumns = [...prev];
            const currentIndex = newColumns.findIndex(col => col.id === columnId);
            
            if (direction === 'up' && currentIndex > 0) {
                [newColumns[currentIndex], newColumns[currentIndex - 1]] = [newColumns[currentIndex - 1], newColumns[currentIndex]];
                newColumns[currentIndex].order = currentIndex;
                newColumns[currentIndex - 1].order = currentIndex - 1;
            } else if (direction === 'down' && currentIndex < newColumns.length - 1) {
                [newColumns[currentIndex], newColumns[currentIndex + 1]] = [newColumns[currentIndex + 1], newColumns[currentIndex]];
                newColumns[currentIndex].order = currentIndex;
                newColumns[currentIndex + 1].order = currentIndex + 1;
            }
            
            saveColumnConfigToCache(newColumns);
            return newColumns;
        });
    }

    const handleDogSelection = (dog: any) => {
        setSelectedDog(dog);
    };

    const handleClearSelectedDog = () => {
        setSelectedDog(null);
        // Filters will be handled server-side again, so we don't clear them here
        // They will be preserved if user wants to re-select a dog
    };
    
    // This is the same as in BreedSimulator. Could be made into a global function?
    function createTraitColumn(trait: any) {
        if (trait != undefined) {
            let column: any = {
                label: t("dogs.table." + trait["NAME"]),
                field: trait["COLUMNDBNAME"],
            };
            column["override"] = (dataLine: any) => {
                // Special overrides for specific traits
                if(trait["COLUMNDBNAME"].includes("_EBV")){
                    let columnname = trait["COLUMNDBNAME"];
                    
                    // Replace "EBV" with "ACC" in the column name
                    let accColumnDBName =  columnname.replace("EBV", "ACC");

                    let accValue = dataLine[accColumnDBName];
                    let color = "#e1e855";
    
                    if (accValue > 0.6) {
                        color = "#68DB5A"; // Green
                    } else if (accValue > 0.3) {
                        color =  "#e1e855"; // Yellow
                    } else {
                        color =  "#e16a6a"; // Red
                    }
                    return <span
                        style={{
                            color: color,
                            fontWeight: 'bold'
                        }}
                    > 
                        {dataLine[trait["COLUMNDBNAME"]]}
                    </span>
                }

                if(trait["COLUMNDBNAME"].includes("TITLE_CLASSNUM_BEST")){
                    const valuemapping: any = {
                        1: t("dogs.table.titleclassnumbers.best1"),
                        2: t("dogs.table.titleclassnumbers.best2"),
                        3: t("dogs.table.titleclassnumbers.best3"),
                        4: t("dogs.table.titleclassnumbers.best4"),
                        5: t("dogs.table.titleclassnumbers.best5"),
                        6: t("dogs.table.titleclassnumbers.best6"),
                        7: t("dogs.table.titleclassnumbers.best7"),
                        8: t("dogs.table.titleclassnumbers.best8"),
                        9: t("dogs.table.titleclassnumbers.best9"),
                    };

                    // If value exists in mapping, return number + mapped value
                    if (dataLine[trait["COLUMNDBNAME"]] in valuemapping) {
                        return dataLine[trait["COLUMNDBNAME"]] + " - " + valuemapping[dataLine[trait["COLUMNDBNAME"]]];
                    } else {
                        return dataLine[trait["COLUMNDBNAME"]];
                    }
                }

                if(trait["COLUMNDBNAME"].includes("PROS_HD_RONTGET_AVK")){
                    // Add percentage sign
                    let value = dataLine[trait["COLUMNDBNAME"]];
                    if(value == null){
                        return "";
                    }
                    return value + " %";
                }

                return dataLine[trait["COLUMNDBNAME"]];
            }
            if (trait.DATATYPE == "datetime") {
                console.log("Adding date override for trait:", trait);
                if (trait["COLUMNDBNAME"] == "DATE_BIRTH") {
                    // Show age in years
                    column["override"] = (dataLine: any) => {
                        if (dataLine[trait["COLUMNDBNAME"]] == null) {
                            return "Ukjent";
                        }
                        let date = new Date(dataLine[trait["COLUMNDBNAME"]]);
                        let today = new Date();
                        let age = today.getFullYear() - date.getFullYear();
                        let m = today.getMonth() - date.getMonth();
                        if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
                            age--;
                        }
                        return age.toString();
                    };
                    // Search override takes in the age in years, and converts it back to a date for searching
                    // Sample: 1972-05-30 for age 52
                    column["searchoverride"] = (age: any) => {
                        if (!age || isNaN(parseInt(age))) {
                            return "";
                        }
                        let today = new Date();
                        let birthYear = today.getFullYear() - parseInt(age);
                        // Return date string in YYYY-MM-DD format
                        return `${birthYear}`;
                    };
                    column["label"] = t("dogs.table.AGE");
                } else {
                    column["override"] = (dataLine: any) => {
                        if (dataLine[trait["COLUMNDBNAME"]] == null) {
                            return "";
                        }
                        let date = new Date(dataLine[trait["COLUMNDBNAME"]]);
                        return date.toLocaleDateString("nb-NO", { month: "2-digit", day: "2-digit", year: "numeric" })
                    };
                }
            }
            if (trait.OPTIONS != null) {
                column["customFilters"] = [
                    ...trait.OPTIONS.split(",").map((option: any) => {
                        // Remove spaces
                        let optionLabel = option?.replace(/\s/g, '') ?? "null";
                        return {
                            label: t("dogs.table.customfilteroptions." + optionLabel),
                            value: option
                        }
                    })
                ];
                column["override"] = (dataLine: any) => {
                    // Remove spaces
                    let labelOverride = dataLine[trait["COLUMNDBNAME"]]?.replace(/\s/g, '') ?? "null";
                    return t("dogs.table.customfilteroptions." + labelOverride);
                };
            }
            return column;
        } else {
            return undefined;
        }
    }

    function createSelectedTraitColumns() {
        if (selectedTraits.length == 0 || availableTraits.length == 0) {
            return [];
        }
        let columns: any[] = [];
        selectedTraits.forEach((trait: string) => {
            columns.push(createTraitColumn(availableTraits.find(i => i["COLUMNDBNAME"] == trait)));
        });
        return columns;
    }

    function getTraitsByBreed(breedId: string) {
        fetch(`/api/dogs/publictraits/${breedId}`)
            .then(response => response.json())
            .then(traits => {
                // Order traits by EXTERNAL_ORDER field. put 0 at the end
                traits.sort((a: any, b: any) => {
                    if (a.EXTERNAL_ORDER === 0) return 1;
                    if (b.EXTERNAL_ORDER === 0) return -1;
                    return a.EXTERNAL_ORDER - b.EXTERNAL_ORDER;
                });

                setAvailableTraits(traits || []);

                // Default selected traits
                const defaultTraitNames: string[] = [
                    'REGNO',
                    'NAME',
                    'SEX',
                    'HD_INDEX',
                    'RELATIONSHIP_INDEX',
                    'ATAKSI_RESULT',
                    'DWARF',
                    'GLAUKOM',
                    'ANTALL_AVKOM',
                    'TITLE_CLASS',
                    'LEASH_M1_EBV',
                    'LEASH_M2_EBV',
                    'HIGHEST_BAND_SCORE'
                ];
                const defaultSelected = traits?.map((trait: any) => trait.COLUMNDBNAME).filter((name: string) => defaultTraitNames.includes(name)) || [];

                // Filter defaultSelected to only include traits that are in localStorage if exists.
                const cachedSelectedTraits = localStorage.getItem("externalportal.breeding.selectedTraits." + selectedBreed);
                if (cachedSelectedTraits) {
                    const parsed = JSON.parse(cachedSelectedTraits);
                    const validSelected = parsed.filter((name: string) => traits.some((trait: any) => trait.COLUMNDBNAME == name));
                    setSelectedTraits(validSelected);
                } else {
                    setSelectedTraits(defaultSelected);
                }
            })
            .catch(error => {
                console.error("Error fetching traits for breed:", error);
            });
    }

    useEffect(() => {
        if (selectedBreed) {
            getTraitsByBreed(selectedBreed);
        }
    }, [selectedBreed]);

    return (
        <div 
            className={styles.breedingDogs}
            style={{ '--background-image': `url(${backgroundImage})` } as React.CSSProperties}
        >
            <div className={styles.backgroundTail}></div>

            {selectedBreed && <><div className={styles.mainContent}>
                <div className={styles.pageHeader}>
                    <div className={styles.headerTop}>
                        <h1>Avlsportal</h1>
                        {selectedDog && (
                            <div className={styles.headerActions}>
                                <button 
                                    className={styles.configureColumns}
                                    onClick={() => setShowColumnModal(true)}
                                >
                                    <span className={styles.gearIcon}>⚙️</span>
                                    Konfigurer kolonner
                                </button>
                            </div>
                        )}
                    </div>
                    
                    {/* Top Controls Row */}
                    <div className={styles.topControlsRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', margin: '10px 0' }}>
                    {/* Selected Dog Button */}
                        <div className={styles.selectedDogButtonContainer}>
                        {selectedDog ? (
                        <button 
                            className={styles.velgHundButton}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                background: '#4CAF50',
                                border: 'none',
                                    borderRadius: '20px',
                                padding: '8px 16px',
                                fontSize: '13px',
                                color: '#FFFFFF',
                                cursor: 'pointer',
                                transition: 'background 0.2s ease',
                                width: 'fit-content',
                                minWidth: 'auto'
                            }}
                            onClick={() => setShowDogSelectionModal(true)}
                        >
                                <span className={styles.selectedDogText}>
                                    Valgt hund: {selectedDog.NAME || 'Ukjent navn'} ({selectedDog.REGNO || 'Ukjent reg.nr'})
                                </span>
                                <span
                                    className={styles.clearDogButton}
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid white',
                                        borderRadius: '50%',
                                        color: 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        height: '20px',
                                        width: '20px',
                                        transition: 'all 0.2s ease',
                                        flexShrink: 0,
                                        marginLeft: '8px',
                                        opacity: 1
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClearSelectedDog();
                                    }}
                                    title="Fjern valgt hund"
                                >
                                    ×
                                </span>
                            </button>
                        ) : (
                            <button 
                                className={styles.velgHundButton}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    background: '#4CAF50',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '8px 16px',
                                    fontSize: '13px',
                                    color: '#FFFFFF',
                                    cursor: 'pointer',
                                    transition: 'background 0.2s ease',
                                    width: 'fit-content',
                                    minWidth: 'auto'
                                }}
                                onClick={() => {
                                    setShowDogSelectionModal(true);
                                    setHasSearched(false);
                                    setSelectDog(true);
                                }}
                            >
                                Velg hund
                            </button>
                        )}
                        </div>

                        {/* View Controls */}
                        <div className={styles.topControlsRight} style={{ display: 'flex', alignItems: 'center', gap: '20px', marginLeft: 'auto' }}>
                            <div className={styles.viewToggle}>
                                <div className={styles.viewToggleButtons} style={{ display: 'flex', gap: '8px' }}>
                                    <button 
                                        className={`${styles.viewToggleButton} ${viewMode === 'list' ? styles.active : ''}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            padding: '8px 12px',
                                            border: '1px solid #D0D0D0',
                                            borderRadius: '6px',
                                            background: viewMode === 'list' ? '#4CAF50' : '#FFFFFF',
                                            borderColor: viewMode === 'list' ? '#4CAF50' : '#D0D0D0',
                                            color: viewMode === 'list' ? '#FFFFFF' : '#000000',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            transition: 'all 0.2s ease',
                                            minWidth: '40px',
                                            height: '40px'
                                        }}
                                        onClick={() => {
                                            setViewMode('list');
                                            saveViewModeToCache('list');
                                        }}
                                    >
                                        <span className={styles.viewToggleIcon}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                <rect x="2" y="3" width="10" height="1.5" fill="currentColor"/>
                                                <rect x="2" y="7" width="10" height="1.5" fill="currentColor"/>
                                                <rect x="2" y="11" width="10" height="1.5" fill="currentColor"/>
                                            </svg>
                                        </span>
                                    </button>
                                    <button 
                                        className={`${styles.viewToggleButton} ${viewMode === 'card' ? styles.active : ''}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            padding: '8px 12px',
                                            border: '1px solid #D0D0D0',
                                            borderRadius: '6px',
                                            background: viewMode === 'card' ? '#4CAF50' : '#FFFFFF',
                                            borderColor: viewMode === 'card' ? '#4CAF50' : '#D0D0D0',
                                            color: viewMode === 'card' ? '#FFFFFF' : '#000000',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            transition: 'all 0.2s ease',
                                            minWidth: '40px',
                                            height: '40px'
                                        }}
                                        onClick={() => {
                                            setViewMode('card');
                                            saveViewModeToCache('card');
                                        }}
                                    >
                                        <span className={styles.viewToggleIcon}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                <rect x="2" y="2" width="5" height="5" stroke="currentColor" strokeWidth="1" fill="none"/>
                                                <rect x="9" y="2" width="5" height="5" stroke="currentColor" strokeWidth="1" fill="none"/>
                                                <rect x="2" y="9" width="5" height="5" stroke="currentColor" strokeWidth="1" fill="none"/>
                                                <rect x="9" y="9" width="5" height="5" stroke="currentColor" strokeWidth="1" fill="none"/>
                                            </svg>
                                        </span>
                                    </button>
                                    <button 
                                        className={`${styles.viewToggleButton} ${viewMode === 'map' ? styles.active : ''}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            padding: '8px 12px',
                                            border: '1px solid #D0D0D0',
                                            borderRadius: '6px',
                                            background: viewMode === 'map' ? '#4CAF50' : '#FFFFFF',
                                            borderColor: viewMode === 'map' ? '#4CAF50' : '#D0D0D0',
                                            color: viewMode === 'map' ? '#FFFFFF' : '#000000',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            transition: 'all 0.2s ease',
                                            minWidth: '40px',
                                            height: '40px'
                                        }}
                                        onClick={() => {
                                            setViewMode('map');
                                            saveViewModeToCache('map');
                                        }}
                                    >
                                        <span className={styles.viewToggleIcon}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                <path d="M8 2C5.5 2 3.5 4 3.5 6.5C3.5 9.5 8 14 8 14S12.5 9.5 12.5 6.5C12.5 4 10.5 2 8 2ZM8 8.5C7.2 8.5 6.5 7.8 6.5 7C6.5 6.2 7.2 5.5 8 5.5C8.8 5.5 9.5 6.2 9.5 7C9.5 7.8 8.8 8.5 8 8.5Z" fill="currentColor"/>
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Search section - shown always, filters only when dog is selected */}
                    {/* <div className={styles.filterSection}>
                        <div className={styles.filterLeft}>
                            <div className={styles.searchSection} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <button 
                                    className={styles.toggleFiltersButton}
                                    style={{
                                        padding: '8px 16px',
                                        background: selectedDog ? '#4CAF50' : '#6c757d',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        flexShrink: 0
                                    }}
                                    onClick={() => {
                                        if (selectedDog) {
                                            setShowFilters(!showFilters);
                                        } else {
                                            setRangeFiltersOpen(!rangeFiltersOpen);
                                        }
                                    }}
                                >
                                    <span className={styles.filterIcon}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                            <rect x="2" y="3" width="12" height="1.5" rx="0.75" fill="currentColor"/>
                                            <circle cx="10" cy="3.75" r="1.5" fill="white" stroke="currentColor" strokeWidth="1"/>
                                            <rect x="2" y="7" width="12" height="1.5" rx="0.75" fill="currentColor"/>
                                            <circle cx="8" cy="7.75" r="1.5" fill="white" stroke="currentColor" strokeWidth="1"/>
                                            <rect x="2" y="11" width="12" height="1.5" rx="0.75" fill="currentColor"/>
                                            <circle cx="6" cy="11.75" r="1.5" fill="white" stroke="currentColor" strokeWidth="1"/>
                                        </svg>
                                    </span>
                                    Filter
                                </button>
                                <input
                                    type="text"
                                    placeholder="Søk etter navn, reg.nr eller eier..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    className={styles.searchInput}
                                    style={{ flex: 1 }}
                                />
                            </div>
                            
                            {!selectedDog && rangeFiltersOpen && (
                                <div className={styles.rangeFiltersArea}>
                                    <div className={styles.rangeFilters}>
                                        <div className={styles.rangeFilterField}>
                                            <label>Feltnavn</label>
                                            <Form.Select
                                                value={rangeFilterField}
                                                onChange={(e) => setRangeFilterField(e.target.value)}
                                            >
                                                <option value="">Velg felt</option>
                                                {availableColumns
                                                    .filter(col => col.visible)
                                                    .map(col => (
                                                        <option key={col.id} value={col.id}>
                                                            {col.label}
                                                        </option>
                                                    ))}
                                            </Form.Select>
                                        </div>
                                        <div className={styles.rangeFilterFrom}>
                                            <label>Fra</label>
                                            <Form.Control
                                                type="text"
                                                placeholder="X"
                                                value={rangeFilterFrom}
                                                onChange={(e) => setRangeFilterFrom(e.target.value)}
                                            />
                                        </div>
                                        <div className={styles.rangeFilterTo}>
                                            <label>Til</label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Y"
                                                value={rangeFilterTo}
                                                onChange={(e) => setRangeFilterTo(e.target.value)}
                                            />
                                        </div>
                                        <div className={styles.addFilterButtonContainer}>
                                            <button
                                                className={styles.addFilterButton}
                                                disabled={!rangeFilterField || (!rangeFilterFrom && !rangeFilterTo)}
                                                onClick={() => {
                                                    if (rangeFilterField && (rangeFilterFrom || rangeFilterTo)) {
                                                        setActiveRangeFilters([...activeRangeFilters, {
                                                            field: rangeFilterField,
                                                            from: rangeFilterFrom,
                                                            to: rangeFilterTo
                                                        }]);
                                                        setRangeFilterField('');
                                                        setRangeFilterFrom('');
                                                        setRangeFilterTo('');
                                                    }
                                                }}
                                            >
                                                Legg til filter
                                            </button>
                                        </div>
                                    </div>
                                    {activeRangeFilters.length > 0 && (
                                        <div className={styles.activeFiltersList}>
                                            {activeRangeFilters.map((filter, index) => {
                                                const column = availableColumns.find(col => col.id === filter.field);
                                                return (
                                                    <div key={index} className={styles.activeFilter}>
                                                        <span className={styles.activeFilterName}>
                                                            {column?.label || filter.field} mellom {filter.from || '...'} - {filter.to || '...'}
                                                        </span>
                                                        <button
                                                            className={styles.removeFilterButton}
                                                            onClick={() => {
                                                                setActiveRangeFilters(activeRangeFilters.filter((_, i) => i !== index));
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {selectedDog && showFilters && (
                                <>
                                    <label>Filtrer etter</label>
                                    <div className={styles.filterRow}>
                                        {dynamicFilters.map(filter => renderDynamicFilter(filter))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div> */}
                </div>

                {/* {loading ? (
                    <div className={styles.loading}>
                        <p>Henter hunder...</p>
                    </div>
                ) : dogsWithSimulatedData.length > 0 ? ( */}
                {
                    (viewMode === 'list' || viewMode === 'card' || viewMode === 'map') && !selectedDog ? (
                        <div className={styles.tableContainer}>
                            <div className={styles.tableControls}>
                                <div className={styles.tablecontrolsleft}>
                                    <div className={styles.filtertoggles}>
                                        <div
                                            className={styles.showonlybreedingdogsbtn}
                                            onClick={() => {
                                                const newValue = !showOnlyApproveddogs;
                                                if (newValue && includeDogsOnWait) {
                                                    // If turning on approved dogs, turn off dogs on wait
                                                    setIncludeDogsOnWait(false);
                                                    localStorage.setItem("externaldogs.includeDogsOnWait", JSON.stringify(false));
                                                }
                                                setShowOnlyApproveddogs(newValue);
                                                localStorage.setItem("dogs.showOnlyApproveddogs", JSON.stringify(newValue));
                                            }}
                                        >
                                            { showOnlyApproveddogs ? 
                                                <>
                                                    <p>{t("dogs.showonlyapproveddogs")}</p>
                                                    <i className="far fa-check-square"></i>
                                                </>
                                            :
                                                <>
                                                    <p>{t("dogs.showonlyapproveddogs")}</p>
                                                    <i className="far fa-square"></i>
                                                </>
                                            }
                                        </div>
                                        <div
                                            className={styles.showonlybreedingdogsbtn}
                                            onClick={() => {
                                                const newValue = !includeDogsOnWait;
                                                if (newValue && showOnlyApproveddogs) {
                                                    // If turning on dogs on wait, turn off approved dogs
                                                    setShowOnlyApproveddogs(false);
                                                    localStorage.setItem("dogs.showOnlyApproveddogs", JSON.stringify(false));
                                                }
                                                setIncludeDogsOnWait(newValue);
                                                localStorage.setItem("externaldogs.includeDogsOnWait", JSON.stringify(newValue));
                                            }}
                                        >
                                            { includeDogsOnWait ? 
                                                <>
                                                    <p>Hunder på vent</p>
                                                    <i className="far fa-check-square"></i>
                                                </>
                                            :
                                                <>
                                                    <p>Hunder på vent</p>
                                                    <i className="far fa-square"></i>
                                                </>
                                            }
                                        </div>
                                        {selectedBreed == "145" ? <div className={styles.disciplinetoggle}>
                                            <p>{t("externalportal.breeding.disciplinefilter.title")}</p>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                {(['all', 'leash', 'loose'] as const).map((val) => (
                                                    <span
                                                        key={val}
                                                        onClick={() => {
                                                            setSelectedDiscipline(val);
                                                            localStorage.setItem("externaldogs.discipline", val);
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                            background: selectedDiscipline === val ? '#4CAF50' : '#e0e0e0',
                                                            color: selectedDiscipline === val ? '#fff' : '#333',
                                                            fontWeight: selectedDiscipline === val ? 600 : 400,
                                                        }}
                                                    >
                                                        {val === 'all' ? 'Alle' : val === 'leash' ? 'Bandhund' : 'Løshund'}
                                                    </span>
                                                ))}
                                            </div>
                                        </div> : null}
                                    </div>
                                    <button 
                                        className={styles.specialfiltersbutton}
                                        onClick={() => setSpecialFiltersOpen(!specialFiltersOpen)}
                                    >
                                        {t("externalportal.breeding.specialfiltersbutton") }    
                                    </button>
                                </div>
                                <div className={styles.tablecontrolsright}>
                                    <button
                                        className={styles.configurecolumnsbtn}
                                        onClick={() => {
                                            setShowTraitsModal(true);
                                        }}
                                    >
                                        <p>{t("dogs.configurecolumnsbtn")}</p>
                                        <i className="fa fa-cog"></i>
                                    </button>
                                </div>
                            </div>
                            { specialFiltersOpen ?
                                <div className={styles.specialfiltersarea}>
                                    <div className={styles.specialfilters}>
                                        <div>
                                            <h3>{t("dogs.specialfilters.fieldnameselector.title")}</h3>
                                            <Field
                                                type="select"
                                                value={{ value: specialfiltersfield, label: t("dogs.table." + availableTraits.find(i => i["COLUMNDBNAME"] == specialfiltersfield)?.NAME) }}
                                                onChange={ (val: any) => {
                                                    setSpecialfiltersfield(val["value"]);
                                                }}
                                                options={{
                                                    choosetext: t("dogs.specialfilters.fieldnameselector.placeholder"),
                                                    // Selected subset of available traits
                                                    choices: availableTraits.filter(i => i.LINEARFILTERING == 1).map((trait: any) => {
                                                        return {
                                                            value: trait["COLUMNDBNAME"],
                                                            label: t("dogs.table." + trait["NAME"])
                                                        }
                                                    })
                                                }}
                                            ></Field>
                                        </div>
                                        <div>
                                            <h3>{t("dogs.specialfilters.fromselector.title")}</h3>
                                            <input
                                                type="text"
                                                id="specialfilters.fromselector"
                                                placeholder={t("dogs.specialfilters.fromselector.placeholder")}
                                                value={specialfiltersfrom}
                                                onChange={(e) => {
                                                    setSpecialfiltersfrom(e.target.value);
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <h3>{t("dogs.specialfilters.toselector.title")}</h3>
                                            <input
                                                type="text"
                                                id="specialfilters.toselector"
                                                placeholder={t("dogs.specialfilters.toselector.placeholder")}
                                                value={specialfiltersto}
                                                onChange={(e) => {
                                                    setSpecialfiltersto(e.target.value);
                                                }}
                                            />
                                        </div>
                                        <div className={styles.addfilterbtncontainer}>
                                            <button
                                                className={
                                                    styles.button + " " +
                                                    styles.addfilterbtn + " " +
                                                    (specialfiltersfield == "" || (specialfiltersfrom == "" && specialfiltersto == "") ?
                                                        styles.disabled
                                                    : "")
                                                }
                                                onClick={() => {
                                                    if (specialfiltersfield == "" || (specialfiltersfrom == "" && specialfiltersto == "")) {
                                                        return;
                                                    }
                                                    if (specialfilters == undefined) {
                                                        setSpecialfilters([{
                                                            field: specialfiltersfield,
                                                            type: "between",
                                                            value: specialfiltersfrom + " - " + specialfiltersto
                                                        }]);
                                                    } else {
                                                        setSpecialfilters([...specialfilters, {
                                                            field: specialfiltersfield,
                                                            type: "between",
                                                            value: specialfiltersfrom + " - " + specialfiltersto
                                                        }]);
                                                    }
                                                    setSpecialfiltersfield("");
                                                    setSpecialfiltersfrom("");
                                                    setSpecialfiltersto("");
                                                }}
                                            >{t("dogs.specialfilters.addfilterbtn")}</button>
                                        </div>
                                    </div>
                                    <div className={styles.specialfilterslist}>
                                        { specialfilters != undefined && specialfilters.length > 0 ?
                                            specialfilters.map((filter: any, index: any) => 
                                                <div className={styles.specialfilter} key={index}>
                                                    <div className={styles.specialfiltername}>
                                                        {t("dogs.table." + availableTraits.find(i => i["COLUMNDBNAME"] == filter["field"])?.NAME)} {t("dogs.specialfilters." + filter["type"])} {filter["value"]}
                                                    </div>
                                                    <div
                                                        className={styles.specialfilterremove}
                                                        onClick={() => {
                                                            setSpecialfilters(specialfilters.filter(i => i != filter));
                                                        }}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </div>
                                                </div>
                                            )
                                        : null }
                                    </div>
                                </div>
                            : <div>
                                { specialfilters != undefined && specialfilters.length > 0 ?
                                    <div className={styles.specialfiltersapplied}>
                                        {specialfilters.map((filter: any, key: any) => {
                                            return (
                                                <div className={styles.specialfilter} key={key}>
                                                    <div className={styles.specialfiltername}>
                                                        {t("dogs.table." + availableTraits.find(i => i["COLUMNDBNAME"] == filter["field"])?.NAME)} {t("dogs.specialfilters." + filter["type"])} {filter["value"]}
                                                    </div>
                                                    <div
                                                        className={styles.specialfilterremove}
                                                        onClick={() => {
                                                            setSpecialfilters(specialfilters.filter(i => i != filter));
                                                        }}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                : null }
                            </div> }
                            <PageTable
                                dataUrl="/api/dogs/getAllPaged/public"
                                viewMode={viewMode}
                                mapLocationField="POSTAL_CODE"
                                //saveFiltersKey={"externaldogs.table." + selectedBreed}
                                onLineClick={(dataLine: any) => {
                                    setSelectedDog(dataLine);
                                }}
                                options={{
                                    showFields: [
                                        ...createSelectedTraitColumns(),
                                    ],
                                }}
                                // onDataFetchStarted={() => {
                                //     setTotalDogs(undefined);
                                // }}
                                // onDataFetched={(data: any) => {
                                //     setTotalDogs(data["totalcount"]);
                                // }}
                                // Order by EXTERNAL_ORDER in availableTraits. 0 at end
                                selectedTraits={Array.from(new Set(['REGNO', ...selectedTraits])).sort((a, b) => {
                                    const traitA = availableTraits.find(i => i["COLUMNDBNAME"] == a);
                                    const traitB = availableTraits.find(i => i["COLUMNDBNAME"] == b);
                                    return (traitA?.EXTERNAL_ORDER || 9999) - (traitB?.EXTERNAL_ORDER || 9999);
                                })}
                                // showOnlyBreedingdogs={showOnlyBreedingdogs}
                                showOnlyApproveddogs={showOnlyApproveddogs}
                                includeDogsOnWait={includeDogsOnWait}
                                // resetFilters={resetFiltersTrigger}
                                permaFilters={[
                                    {
                                        field: "BREED",
                                        exact: false,
                                        type: "and",
                                        values: [selectedBreed?.toString() || ""]
                                    },
                                    ...(specialfilters != undefined && specialfilters.length > 0 ?
                                        specialfilters.map((filter: any) => {
                                            return {
                                                field: filter["field"],
                                                exact: false,
                                                type: "between",
                                                values: [filter["value"]]
                                            }
                                        }
                                    ) : []),
                                    ...(selectedBreed == "145" && selectedDiscipline !== 'all' ? [{
                                        field: "PUBLISHED_PUBLIC",
                                        exact: true,
                                        type: "and",
                                        values: [selectedDiscipline]
                                    }] : []),
                                ].filter(i => i != null)}
                                clickable
                                defaultSortDirection="desc"
                                defaultSortField="ANIMAL_ID"
                                hidePagination={selectedDog ? true : false}
                                hidePagesize={false}
                                hideFilter={false}
                                hideExportExcel={true}
                            />
                        </div>
                    ) : selectedDog && simulatedBreedingLoading ? (
                        <div className={styles.loading}>
                            <p>Simulerer avl...</p>
                        </div>
                    ) : selectedDog ? (
                        <div className={styles.tableContainer}>
                            <div className={styles.tableControls}>
                                <div className={styles.tablecontrolsleft}>
                                    <button 
                                        className={styles.specialfiltersbutton}
                                        onClick={() => setClientSpecialFiltersOpen(!clientSpecialFiltersOpen)}
                                    >
                                        {t("externalportal.breeding.specialfiltersbutton")}
                                    </button>
                                </div>
                            </div>
                            { clientSpecialFiltersOpen ?
                                <div className={styles.specialfiltersarea}>
                                    <div className={styles.specialfilters}>
                                        <div>
                                            <h3>{t("dogs.specialfilters.fieldnameselector.title")}</h3>
                                            <Field
                                                type="select"
                                                value={{ value: clientSpecialFiltersField, label: availableColumns.find(i => i.id === clientSpecialFiltersField)?.label || "" }}
                                                onChange={ (val: any) => {
                                                    setClientSpecialFiltersField(val["value"]);
                                                }}
                                                options={{
                                                    choosetext: t("dogs.specialfilters.fieldnameselector.placeholder"),
                                                    choices: availableColumns
                                                        .filter(col =>
                                                            col.visible &&
                                                            ![
                                                                'NAME',
                                                                'TITLE_CLASS',
                                                                'REGNO',
                                                                'SEX',
                                                                'DATE_BIRTH',
                                                                'OWNER_NAME',
                                                                'PHONE',
                                                                'EMAIL',
                                                                'GLAUKOM',
                                                                'ATAKSI',
                                                                'DWARF',
                                                            ].includes(col.id))
                                                        .map((col: any) => {
                                                            return {
                                                                value: col.id,
                                                                label: col.label
                                                            }
                                                        })
                                                }}
                                            ></Field>
                                        </div>
                                        <div>
                                            <h3>{t("dogs.specialfilters.fromselector.title")}</h3>
                                            <input
                                                type="text"
                                                id="clientspecialfilters.fromselector"
                                                placeholder={t("dogs.specialfilters.fromselector.placeholder")}
                                                value={clientSpecialFiltersFrom}
                                                onChange={(e) => {
                                                    setClientSpecialFiltersFrom(e.target.value);
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <h3>{t("dogs.specialfilters.toselector.title")}</h3>
                                            <input
                                                type="text"
                                                id="clientspecialfilters.toselector"
                                                placeholder={t("dogs.specialfilters.toselector.placeholder")}
                                                value={clientSpecialFiltersTo}
                                                onChange={(e) => {
                                                    setClientSpecialFiltersTo(e.target.value);
                                                }}
                                            />
                                        </div>
                                        <div className={styles.addfilterbtncontainer}>
                                            <button
                                                className={
                                                    styles.button + " " +
                                                    styles.addfilterbtn + " " +
                                                    (clientSpecialFiltersField === "" || (clientSpecialFiltersFrom === "" && clientSpecialFiltersTo === "") ?
                                                        styles.disabled
                                                    : "")
                                                }
                                                onClick={() => {
                                                    if (clientSpecialFiltersField === "" || (clientSpecialFiltersFrom === "" && clientSpecialFiltersTo === "")) {
                                                        return;
                                                    }
                                                    setClientSpecialFilters([...clientSpecialFilters, {
                                                        field: clientSpecialFiltersField,
                                                        from: clientSpecialFiltersFrom,
                                                        to: clientSpecialFiltersTo
                                                    }]);
                                                    setClientSpecialFiltersField("");
                                                    setClientSpecialFiltersFrom("");
                                                    setClientSpecialFiltersTo("");
                                                }}
                                            >{t("dogs.specialfilters.addfilterbtn")}</button>
                                        </div>
                                    </div>
                                    <div className={styles.specialfilterslist}>
                                        { clientSpecialFilters.length > 0 ?
                                            clientSpecialFilters.map((filter: any, index: any) => {
                                                const column = availableColumns.find(col => col.id === filter.field);
                                                return (
                                                    <div className={styles.specialfilter} key={index}>
                                                        <div className={styles.specialfiltername}>
                                                            {column?.label || filter.field} {t("dogs.specialfilters.between")} {filter.from || '...'} - {filter.to || '...'}
                                                        </div>
                                                        <div
                                                            className={styles.specialfilterremove}
                                                            onClick={() => {
                                                                setClientSpecialFilters(clientSpecialFilters.filter((f, i) => i !== index));
                                                            }}
                                                        >
                                                            <i className="fa fa-times"></i>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        : null }
                                    </div>
                                </div>
                            : <div>
                                { clientSpecialFilters.length > 0 ?
                                    <div className={styles.specialfiltersapplied}>
                                        {clientSpecialFilters.map((filter: any, key: any) => {
                                            const column = availableColumns.find(col => col.id === filter.field);
                                            return (
                                                <div className={styles.specialfilter} key={key}>
                                                    <div className={styles.specialfiltername}>
                                                        {column?.label || filter.field} {t("dogs.specialfilters.between")} {filter.from || '...'} - {filter.to || '...'}
                                                    </div>
                                                    <div
                                                        className={styles.specialfilterremove}
                                                        onClick={() => {
                                                            setClientSpecialFilters(clientSpecialFilters.filter((f, i) => i !== key));
                                                        }}
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                : null }
                            </div> }
                            <PublicTable
                                headers={getTableHeaders()}
                                data={getPaginatedData()}
                                viewMode={viewMode}
                                mapLocationField="POSTAL_CODE"
                                onClickRow={(data) => {
                                    if (selectedDog) {
                                        setShowBreedingRequestModal(data);
                                    }
                                }}
                            />
                        </div>
                    ) : null
                }
                {dogsWithSimulatedData.length > 0 && viewMode !== 'map' && selectedDog && (
                    <div className={styles.paginationControls}>
                        <div className={styles.rowsPerPageSelector}>
                            <select
                                id="rowsPerPage"
                                value={selectedDog ? rowsPerPage : serverPageSize}
                                onChange={(e) => {
                                    const newPageSize = Number(e.target.value);
                                    if (selectedDog) {
                                        setRowsPerPage(newPageSize);
                                        setCurrentPage(1);
                                    } else {
                                        setServerPageSize(newPageSize);
                                        setServerPage(0); // Reset to first page
                                    }
                                }}
                                className={styles.rowsPerPageSelect}
                            >
                                <option value={15}>15</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        
                        {/* Display total count */}
                        <div className={styles.totalCount}>
                            <span>Antall hunder: {selectedDog ? totalDogs : totalDogsCount}</span>
                        </div>
                        
                    <div className={styles.pagination}>
                        <button
                            onClick={() => {
                                if (selectedDog) {
                                    setCurrentPage(Math.max(1, currentPage - 1));
                                } else {
                                    setServerPage(Math.max(0, serverPage - 1));
                                }
                            }}
                            disabled={selectedDog ? currentPage === 1 : serverPage === 0}
                            className={styles.paginationButton}
                        >
                            Forrige
                        </button>
                        
                        <div className={styles.pageNumbers}>
                            {selectedDog ? (
                                // Client-side pagination when dog is selected
                                Array.from({ length: Math.ceil(totalDogs / rowsPerPage) }, (_, i) => i + 1)
                                    .slice(Math.max(0, currentPage - 3), Math.min(Math.ceil(totalDogs / rowsPerPage), currentPage + 2))
                                    .map(page => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`${styles.pageNumber} ${page === currentPage ? styles.active : ''}`}
                                        >
                                            {page}
                                        </button>
                                    ))
                            ) : (
                                // Server-side pagination when no dog is selected
                                Array.from({ length: Math.ceil(totalDogsCount / serverPageSize) }, (_, i) => i + 1)
                                    .slice(Math.max(0, serverPage - 2), Math.min(Math.ceil(totalDogsCount / serverPageSize), serverPage + 3))
                                    .map(page => (
                                        <button
                                            key={page}
                                            onClick={() => setServerPage(page - 1)}
                                            className={`${styles.pageNumber} ${page === serverPage + 1 ? styles.active : ''}`}
                                        >
                                            {page}
                                        </button>
                                    ))
                            )}
                        </div>
                        
                        <button
                            onClick={() => {
                                if (selectedDog) {
                                    setCurrentPage(Math.min(Math.ceil(totalDogs / rowsPerPage), currentPage + 1));
                                } else {
                                    const maxPage = Math.ceil(totalDogsCount / serverPageSize) - 1;
                                    setServerPage(Math.min(maxPage, serverPage + 1));
                                }
                            }}
                            disabled={selectedDog 
                                ? currentPage === Math.ceil(totalDogs / rowsPerPage)
                                : serverPage >= Math.ceil(totalDogsCount / serverPageSize) - 1
                            }
                            className={styles.paginationButton}
                        >
                            Neste
                        </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Traits Selection Modal */}
            <Modal
                show={showTraitsModal}
                onHide={() => setShowTraitsModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t("externalportal.breeding.availabletraits.title")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div
                        className={styles.availabletraitslist}
                    >
                        { availableTraits.sort(
                            // Order by EXTERNAL_ORDER 0 at end
                            (a, b) => {
                                const orderA = a.EXTERNAL_ORDER || 9999;
                                const orderB = b.EXTERNAL_ORDER || 9999;
                                return orderA - orderB;
                            }
                        ).map((trait: any) => {
                            return (
                                <div
                                    key={trait["ID"]}
                                    className={styles.trait}
                                    onClick={() => {
                                        if (selectedTraits.includes(trait["COLUMNDBNAME"])) {
                                            setSelectedTraits(selectedTraits.filter(i => i != trait["COLUMNDBNAME"]));
                                            localStorage.setItem("externalportal.breeding.selectedTraits." + selectedBreed, JSON.stringify(selectedTraits.filter(i => i != trait["COLUMNDBNAME"])));
                                        } else {
                                            setSelectedTraits([...selectedTraits, trait["COLUMNDBNAME"]]);
                                            localStorage.setItem("externalportal.breeding.selectedTraits." + selectedBreed, JSON.stringify([...selectedTraits, trait["COLUMNDBNAME"]]));
                                        }
                                    }}
                                >
                                    <div className={styles.traitname}>
                                        {t("externalportal.breeding.table." + trait["NAME"])}
                                    </div>
                                    <div className={styles.traitselected}>
                                        {selectedTraits.includes(trait["COLUMNDBNAME"]) ? <i className="fa fa-check"></i> : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
            </Modal>

            {/* Column Configuration Modal */}
            <Modal show={showColumnModal} onHide={() => setShowColumnModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Konfigurer kolonner</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.columnConfig}>
                        {availableColumns && availableColumns.length > 0 ? availableColumns
                            .sort((a, b) => {
                                // Show visible columns first, then by order
                                if (a.visible !== b.visible) return b.visible ? 1 : -1;
                                return a.order - b.order;
                            })
                            .map((column, index) => (
                                <div key={column.id} className={styles.columnRow}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`col-${column.id}`}
                                        label={column.label}
                                        checked={column.visible}
                                        onChange={() => handleColumnToggle(column.id)}
                                    />
                                    <div className={styles.columnControls}>
                                        <button
                                            className={styles.orderButton}
                                            onClick={() => handleColumnOrderChange(column.id, 'up')}
                                            disabled={index === 0}
                                        >
                                            ↑
                                        </button>
                                        <button
                                            className={styles.orderButton}
                                            onClick={() => handleColumnOrderChange(column.id, 'down')}
                                            disabled={index === availableColumns.length - 1}
                                        >
                                            ↓
                                        </button>
                                    </div>
                                </div>
                            )) : (
                                <div className={styles.noColumnsMessage}>
                                    Ingen kolonner tilgjengelig. Velg en rase for å se tilgjengelige kolonner.
                                </div>
                            )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowColumnModal(false)}>
                        Lukk
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Breedingrequest Modal */}
            <Modal show={showBreedingRequestModal != null} onHide={() => setShowBreedingRequestModal(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Avlsforespørsel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={styles.breedingrequestmodal}>
                        <div className={styles.comparisonContainer}>
                            <div className={styles.dogcardwrapper}>
                                <div className={styles.dogavatar}>
                                    <span className={styles.dogavatarplaceholder}>
                                        {(selectedDog?.NAME ?? '?')[0]}
                                    </span>
                                    {selectedDog?.ANIMAL_ID && (
                                        <img
                                            src={`/api/dogs/photo/${selectedDog.ANIMAL_ID}`}
                                            alt=""
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    )}
                                </div>
                                <div className={styles.comparisonColumn}>
                                    <div className={styles.comparisonColumnTitle}>Din hund</div>
                                    <hr/>
                                    <div className={styles.fieldlist}>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>Navn</span>
                                            <span className={styles.fieldvalue}>{selectedDog?.NAME || 'Ukjent'}</span>
                                        </div>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>Reg.Nr</span>
                                            <span className={styles.fieldvalue}>{selectedDog?.REGNO || 'Ukjent'}</span>
                                        </div>
                                        {selectedDog?.DATE_BIRTH && (() => {
                                            const birth = new Date(selectedDog.DATE_BIRTH);
                                            const now = new Date();
                                            let age = now.getFullYear() - birth.getFullYear();
                                            const m = now.getMonth() - birth.getMonth();
                                            if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
                                            return (
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>Alder</span>
                                                    <span className={styles.fieldvalue}>{age} år</span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.dogcardwrapper}>
                                <div className={styles.dogavatar}>
                                    <span className={styles.dogavatarplaceholder}>
                                        {(showBreedingRequestModal?.NAME ?? '?')[0]}
                                    </span>
                                    {showBreedingRequestModal?.ANIMAL_ID && (
                                        <img
                                            src={`/api/dogs/photo/${showBreedingRequestModal.ANIMAL_ID}`}
                                            alt=""
                                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                        />
                                    )}
                                </div>
                                <div className={styles.comparisonColumn}>
                                    <div className={styles.comparisonColumnTitle}>Partner hund</div>
                                    <hr/>
                                    <div className={styles.fieldlist}>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>Navn</span>
                                            <span className={styles.fieldvalue}>{showBreedingRequestModal?.NAME || 'Ukjent'}</span>
                                        </div>
                                        <div className={styles.fielditem}>
                                            <span className={styles.fieldlabel}>Reg.Nr</span>
                                            <span className={styles.fieldvalue}>{showBreedingRequestModal?.REGNO || 'Ukjent'}</span>
                                        </div>
                                        {showBreedingRequestModal?.DATE_BIRTH && (() => {
                                            const birth = new Date(showBreedingRequestModal.DATE_BIRTH);
                                            const now = new Date();
                                            let age = now.getFullYear() - birth.getFullYear();
                                            const m = now.getMonth() - birth.getMonth();
                                            if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
                                            return (
                                                <div className={styles.fielditem}>
                                                    <span className={styles.fieldlabel}>Alder</span>
                                                    <span className={styles.fieldvalue}>{age} år</span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.requestInfo}>
                            <DynSchema
                                breedid={selectedBreed}
                                dynschemaid={breedRegSchema?.ID}
                                title=' '
                                description=' '
                                onSchemaSent={() => {
                                    setShowBreedingRequestModal(null);
                                    alert('Avlsforespørsel sendt!');
                                }}
                                chipData={(() => {
                                    const dam = (selectedDog?.SEX === 'female' || selectedDog?.SEX === 'H') ? selectedDog : showBreedingRequestModal;
                                    const sire = (selectedDog?.SEX === 'male' || selectedDog?.SEX === 'T') ? selectedDog : showBreedingRequestModal;
                                    return [
                                        { label: 'Tispas navn', value: dam?.NAME, matchKeywords: ['navn', 'tispa'] },
                                        { label: 'Tispas reg.nr', value: dam?.REGNO, matchKeywords: ['reg', 'tispa'] },
                                        { label: 'Hannhundens navn', value: sire?.NAME, matchKeywords: ['navn', 'hann'] },
                                        { label: 'Hannhundens reg.nr', value: sire?.REGNO, matchKeywords: ['reg', 'hann'] },
                                    ];
                                })()}
                            />
                            {/* Name */}
                            {/* <div className={styles.inputfield}>
                                <Form.Label htmlFor={"inputName"}>Ditt navn</Form.Label>
                                <Form.Control
                                    id={"inputName"}
                                    aria-describedby={"inputNameHelpBlock"}
                                    value={breedingRequestSchemaName}
                                    onChange={(e) => {
                                        setBreedingRequestSchemaName(e.target.value);
                                    }}
                                />
                            </div> */}
                            {/* Phone */}
                            {/* <div className={styles.inputfield}>
                                <Form.Label htmlFor={"inputPhone"}>Telefonnummer</Form.Label>
                                <Form.Control
                                    id={"inputPhone"}
                                    aria-describedby={"inputPhoneHelpBlock"}
                                    value={breedingRequestSchemaPhone}
                                    onChange={(e) => {
                                        setBreedingRequestSchemaPhone(e.target.value);
                                    }}
                                />
                            </div> */}
                            {/* Email */}
                            {/* <div className={styles.inputfield}>
                                <Form.Label htmlFor={"inputEmail"}>E-post adresse</Form.Label>
                                <Form.Control
                                    id={"inputEmail"}
                                    aria-describedby={"inputEmailHelpBlock"}
                                    value={breedingRequestSchemaEmail}
                                    onChange={(e) => {
                                        setBreedingRequestSchemaEmail(e.target.value);
                                    }}
                                />
                            </div> */}
                            {/* Accept Ethics */}
                            {/* <div className={styles.inputfield}>
                                <Form.Label htmlFor={"acceptEthics"}>Jeg bekrefter å kjenne til NKK og Elghundforbundet sine etiske retningslinjer og anbefalinger for sunn avl på Norsk Elghund Sort</Form.Label>
                                <Form.Select
                                    id={"acceptEthics"}
                                    aria-describedby={"acceptEthicsHelpBlock"}
                                    value={breedingRequestSchemaAcceptEthics}
                                    onChange={(e) => {
                                        setBreedingRequestSchemaAcceptEthics(e.target.value);
                                    }}
                                >
                                    <option>{t('dynschema.field.dropdown.select')}</option>
                                    <option value="yes">Ja</option>
                                    <option value="no">Nei</option>
                                </Form.Select>
                            </div> */}
                            {/* Accept Publishing */}
                            {/* <div className={styles.inputfield}>
                                <Form.Label htmlFor={"acceptPublishing"}>Jeg samtykker i at eiers navn, poststed, telefonnummer og e-post adresse publiseres på elghundforbundet sine informasjonskanaler</Form.Label>
                                <Form.Select
                                    id={"acceptPublishing"}
                                    aria-describedby={"acceptPublishingHelpBlock"}
                                    value={breedingRequestSchemaAcceptPublishing}
                                    onChange={(e) => {
                                        setBreedingRequestSchemaAcceptPublishing(e.target.value);
                                    }}
                                >
                                    <option>{t('dynschema.field.dropdown.select')}</option>
                                    <option value="yes">Ja</option>
                                    <option value="no">Nei</option>
                                </Form.Select>
                            </div> */}
                            {/* Accept Information */}
                            {/* <div className={styles.inputfield}>
                                <Form.Label htmlFor={"acceptInformation"}>Jeg samtykker i å motta informasjon fra Elghundforbundet om avl og oppdrett</Form.Label>
                                <Form.Select
                                    id={"acceptInformation"}
                                    aria-describedby={"acceptInformationHelpBlock"}
                                    value={breedingRequestSchemaAcceptInformation}
                                    onChange={(e) => {
                                        setBreedingRequestSchemaAcceptInformation(e.target.value);
                                    }}
                                >
                                    <option>{t('dynschema.field.dropdown.select')}</option>
                                    <option value="yes">Ja</option>
                                    <option value="no">Nei</option>
                                </Form.Select>
                            </div> */}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    {/* <Button
                        variant="primary"
                        disabled={
                            !breedingRequestSchemaName ||
                            !breedingRequestSchemaPhone ||
                            !breedingRequestSchemaEmail ||
                            !breedingRequestSchemaAcceptEthics ||
                            !breedingRequestSchemaAcceptPublishing ||
                            !breedingRequestSchemaAcceptInformation
                        }
                        style={
                            !breedingRequestSchemaName ||
                            !breedingRequestSchemaPhone ||
                            !breedingRequestSchemaEmail ||
                            !breedingRequestSchemaAcceptEthics ||
                            !breedingRequestSchemaAcceptPublishing ||
                            !breedingRequestSchemaAcceptInformation ? { cursor: 'not-allowed', opacity: 0.3  } : { cursor: 'pointer' }
                        }
                        onClick={() => submitBreedingRequest()}
                    >
                        Send avlsforespørsel
                    </Button> */}
                    <Button variant="secondary" onClick={() => setShowBreedingRequestModal(null)}>
                        Lukk
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Dog Selection Modal */}
            <DogSelectionModal
                show={showDogSelectionModal}
                onHide={() => setShowDogSelectionModal(false)}
                dogs={dogs}
                hasSearched={hasSearched}
                onSearchStatusChange={setHasSearched}
                onSelectDog={handleDogSelection}
                selectDog={selectDog}
                setSelectDog={setSelectDog}
                selectedBreed={selectedBreed}
                existingDogs={!selectedDog ? allDogsData : undefined}
                existingTotal={!selectedDog ? totalDogsCount : undefined}
                existingPageSize={!selectedDog ? serverPageSize : undefined}
            />
            </>
            }
        </div>
    );
}

export default BreedingDogs;
